import api from './api'
import type {
  AiMentor,
  AiMentorTemplate,
  AiUsageFilters,
  AiUsageRow,
  MentorAdminLlmStatus,
  MentorAdminOverview,
  MentorAdminRecord,
  MentorAdminSkill,
  MentorCoachImpact,
  MentorLearnerProgression,
  MentorLearningActivity,
  MentorSkillRecommendation,
  MentorLearningPath,
  MentorAssignment,
} from '../types/mentorAdmin'

type AiActionResult = {
  success: boolean
  setupRequired?: boolean
  llmConfigured?: boolean
  result?: Record<string, any> | string
  message?: string
  provider?: string | null
  model?: string | null
  usage?: {
    promptTokens?: number
    completionTokens?: number
    totalTokens?: number
  }
}

type MentorAiActionContext = {
  learnerUserId?: string | null
  learnerProfileId?: string | null
  aiMentorId?: string | null
  assignmentId?: string | null
}

const compactParams = (params: object) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
)

export const mentorAdminService = {
  getOverview: () => api.get<{ success: boolean; overview: MentorAdminOverview }>('/api/mentor-admin/overview'),
  listAiMentorTemplates: () => api.get<{ success: boolean; templates: AiMentorTemplate[] }>('/api/mentor-admin/ai-mentor-templates'),
  listAiMentors: () => api.get<{ success: boolean; aiMentors: AiMentor[] }>('/api/mentor-admin/ai-mentors'),
  createAiMentor: (payload: Partial<AiMentor> & { templateId?: string | null }) => api.post<{ success: boolean; aiMentor: AiMentor }>('/api/mentor-admin/ai-mentors', payload),
  updateAiMentor: (id: string, payload: Partial<AiMentor>) => api.patch<{ success: boolean; aiMentor: AiMentor }>(`/api/mentor-admin/ai-mentors/${id}`, payload),
  listHumanMentors: () => api.get<{ success: boolean; humanMentors: MentorAdminRecord[]; pendingApprovals?: MentorAdminRecord[] }>('/api/mentor-admin/human-mentors'),
  decideHumanMentorClaim: (claimId: string, action: 'approve' | 'reject' | 'suspend', notes?: string | null) => api.post<{ success: boolean; claim: MentorAdminRecord }>(`/api/mentor-admin/human-mentors/${claimId}/${action}`, { notes: notes || null }),
  listLearners: () => api.get<{ success: boolean; learners: MentorAdminRecord[] }>('/api/mentor-admin/learners'),
  listSkills: () => api.get<{ success: boolean; skills: MentorAdminSkill[]; templates: AiMentorTemplate[] }>('/api/mentor-admin/skills'),
  createSkill: (payload: Partial<MentorAdminSkill>) => api.post<{ success: boolean; skill: MentorAdminSkill }>('/api/mentor-admin/skills', payload),
  updateSkill: (id: string, payload: Partial<MentorAdminSkill>) => api.patch<{ success: boolean; skill: MentorAdminSkill }>(`/api/mentor-admin/skills/${id}`, payload),
  listAssignments: () => api.get<{ success: boolean; assignments: MentorAssignment[] }>('/api/mentor-admin/assignments'),
  createAssignment: (payload: Partial<MentorAssignment>) => api.post<{ success: boolean; assignment: MentorAssignment }>('/api/mentor-admin/assignments', payload),
  updateAssignment: (id: string, payload: Partial<MentorAssignment>) => api.patch<{ success: boolean; assignment: MentorAssignment }>(`/api/mentor-admin/assignments/${id}`, payload),
  listLearningPaths: () => api.get<{ success: boolean; learningPaths: MentorLearningPath[] }>('/api/mentor-admin/learning-paths'),
  createLearningPath: (payload: Partial<MentorLearningPath>) => api.post<{ success: boolean; learningPath: MentorLearningPath }>('/api/mentor-admin/learning-paths', payload),
  updateLearningPath: (id: string, payload: Partial<MentorLearningPath>) => api.patch<{ success: boolean; learningPath: MentorLearningPath }>(`/api/mentor-admin/learning-paths/${id}`, payload),
  generateLearningPath: (payload: {
    skillTrack: string
    learnerCurrentLevel: string
    learnerGoal: string
    timeline: string
    learnerUserId?: string | null
    learnerProfileId?: string | null
    aiMentorId?: string | null
    assignmentId?: string | null
    skillId?: string | null
    status?: MentorLearningPath['status']
    saveLearningPath?: boolean
  }) => api.post<{ success: boolean; setupRequired?: boolean; llmConfigured?: boolean; result?: Record<string, any> | string; learningPath?: MentorLearningPath; message?: string }>('/api/mentor-admin/learning-paths/generate', payload),
  generateLesson: (payload: MentorAiActionContext & {
    skillTrack: string
    module: string
    level: string
  }) => api.post<AiActionResult>('/api/mentor-admin/ai/generate-lesson', payload),
  generateQuiz: (payload: MentorAiActionContext & {
    skillTrack: string
    module: string
    level: string
  }) => api.post<AiActionResult>('/api/mentor-admin/ai/generate-quiz', payload),
  runRoleplay: (payload: MentorAiActionContext & {
    skillTrack: string
    scenario: string
    learnerMessage: string
    conversationSummary?: string | null
  }) => api.post<AiActionResult>('/api/mentor-admin/ai/roleplay', payload),
  generateProgressSummary: (payload: MentorAiActionContext & {
    learnerActivity: string
    quizScore?: string | null
    completedModules?: string[]
  }) => api.post<AiActionResult>('/api/mentor-admin/ai/progress-summary', payload),
  getLearnerProgression: (learnerId: string) => api.get<{ success: boolean } & MentorLearnerProgression>(`/api/mentor-admin/learners/${learnerId}/progression`),
  listLearnerRecommendations: (learnerId: string) => api.get<{ success: boolean; learner?: MentorAdminRecord; recommendations: MentorSkillRecommendation[] }>(`/api/mentor-admin/learners/${learnerId}/recommendations`),
  generateLearnerRecommendations: (learnerId: string) => api.post<{ success: boolean } & MentorLearnerProgression>(`/api/mentor-admin/learners/${learnerId}/recommendations/generate`, {}),
  getCoachImpact: (coachId: string) => api.get<{ success: boolean } & MentorCoachImpact>(`/api/mentor-admin/coaches/${coachId}/impact`),
  createCoachingSession: (payload: {
    coachWorkerProfileId: string
    learnerWorkerProfileId: string
    skillId?: string | null
    skillTrack?: string | null
    mentorshipSessionId?: string | null
    startedAt?: string | null
    endedAt?: string | null
    durationMinutes: number
    learnerScoreBefore?: number | null
    learnerScoreAfter?: number | null
    aiSummary?: string | null
    notes?: string | null
    metadata?: Record<string, any>
  }) => api.post<{ success: boolean; coachingSession: MentorAdminRecord }>('/api/mentor-admin/coaching-sessions', payload),
  createLearningActivity: (payload: {
    learnerUserId?: string | null
    learnerProfileId?: string | null
    skillId?: string | null
    skillTrack?: string | null
    assignmentId?: string | null
    aiInteractionId?: string | null
    activityType: MentorLearningActivity['activityType']
    title?: string | null
    score?: number | null
    maxScore?: number | null
    xpEarned?: number
    masteryPercent?: number | null
    durationMinutes?: number | null
    completedAt?: string | null
    metadata?: Record<string, any>
  }) => api.post<{ success: boolean; activity: MentorLearningActivity }>('/api/mentor-admin/learning-activities', payload),
  frenchReadingComprehension: (payload: MentorAiActionContext & {
    learnerLevel: string
    passage?: string | null
    generatedPassageRequest?: string | null
    languagePreference?: 'en' | 'fr'
  }) => api.post<AiActionResult>('/api/mentor-admin/ai/french/reading-comprehension', payload),
  frenchWritingCorrection: (payload: MentorAiActionContext & {
    studentParagraph: string
    assignmentGoal: string
    learnerLevel: string
    languagePreference?: 'en' | 'fr'
  }) => api.post<AiActionResult>('/api/mentor-admin/ai/french/writing-correction', payload),
  frenchDictee: (payload: MentorAiActionContext & {
    learnerLevel: string
    theme: string
    languagePreference?: 'en' | 'fr'
  }) => api.post<AiActionResult>('/api/mentor-admin/ai/french/dictee', payload),
  frenchProgressSummary: (payload: MentorAiActionContext & {
    learnerActivity: string
    completedExercises?: string[]
    quizScores?: string[]
    recurringMistakes?: string[]
    languagePreference?: 'en' | 'fr'
  }) => api.post<AiActionResult>('/api/mentor-admin/ai/french/progress-summary', payload),
  listAiUsage: (filters: AiUsageFilters = {}) => api.get<{ success: boolean; usage: AiUsageRow[] }>('/api/mentor-admin/ai-usage', { params: compactParams(filters) }),
  getLlmStatus: () => api.get<{ success: boolean; llm: MentorAdminLlmStatus }>('/api/mentor-admin/llm-settings/status'),
  listSessions: () => api.get<{ success: boolean; sessions: MentorAdminRecord[] }>('/api/mentor-admin/sessions'),
  listBookings: () => api.get<{ success: boolean; bookings: MentorAdminRecord[] }>('/api/mentor-admin/bookings'),
  listReviews: () => api.get<{ success: boolean; reviews: MentorAdminRecord[] }>('/api/mentor-admin/reviews'),
  listRates: () => api.get<{ success: boolean; rates: MentorAdminRecord[]; platformFeeSettings?: MentorAdminRecord | null }>('/api/mentor-admin/rates'),
  listPayments: () => api.get<{ success: boolean; paymentRecords: MentorAdminRecord[] }>('/api/mentor-admin/payments'),
}
